import { post, get } from "./utils/request";

/**
 * 暂停/创建录制任务请求参数
 * 用于 pauseRecordTask 时，可以传入 task_id 或 cluster_env (根据 API 设计决定哪个是必须的或优先的)
 */
interface CreateRecordTaskRequest {
  /**
   * 集群环境 (创建时必须，暂停时可选)
   */
  cluster_env?: string;
  /**
   * 任务 ID (暂停时可选，用于指定要暂停的任务)
   */
  task_id?: string;
  /**
   * 业务线（产线）
   */
  business?: string;
}

/**
 * 创建流量录制任务
 * @param data - 请求参数
 * @returns 返回创建结果的 Promise (具体类型取决于 API 响应)
 * @throws 如果网络请求或服务器处理失败，则抛出错误 (由 request 工具处理)
 */
async function createRecordTask(
  data: CreateRecordTaskRequest
): Promise<unknown> {
  return post("/apis/v1/traffic/record/task/create", data);
}

/**
 * 暂停流量录制任务
 * @param data - 请求参数 (目前与创建任务相同)
 * @param data - 请求参数，可包含 task_id 或 cluster_env
 * @returns 返回暂停结果的 Promise (具体类型取决于 API 响应)
 * @throws 如果网络请求或服务器处理失败，则抛出错误 (由 request 工具处理)
 */
async function pauseRecordTask(
  data: CreateRecordTaskRequest
): Promise<unknown> {
  // 根据 API 的实际需求，这里可能需要判断是使用 task_id 还是 cluster_env
  // 假设 API 会根据传入的字段自动处理
  return post("/apis/v1/traffic/record/task/pause", data);
}

/**
 * 录制日志条目
 */
interface RecordLogItem {
  business: string;
  cluster_env: string;
  request_body: string;
  request_headers: string;
  request_host: string;
  request_id: string;
  request_path: string;
  response_body: string;
  response_headers: string;
  task_id: string;
  timestamp: string;
  status: string;
  /**
   * 允许未定义的其他属性
   */
  [property: string]: unknown;
}

/**
 * 列表接口的分页和数据结构
 */
interface ListRecordLogData {
  items: RecordLogItem[];
  page_num: number;
  page_size: number;
  total_count: number;
  /**
   * 允许未定义的其他属性
   */
  [property: string]: unknown;
}

/**
 * 获取录制日志列表的完整响应体
 */
interface ListRecordLogResponse {
  data: ListRecordLogData;
  /**
   * 允许未定义的其他属性
   */
  [property: string]: unknown;
}

/**
 * 获取录制日志列表请求参数 (可选)
 */
interface ListRecordLogParams {
  page_num?: number;
  page_size?: number;
  // 可以根据需要添加其他过滤参数, 例如 task_id
  task_id?: string;
  request_id?: string;
  request_headers?: string;
  response_status?: string;
  record_request_id?: string;
}

interface ReplayLogItem {
  task_id?: string;
  request_id: string;
  record_request_id: string;
  meta: string;
  request: string;
  response: string;
  [property: string]: unknown;
}

interface ListReplayLogData {
  items: ReplayLogItem[];
  page_num: number;
  page_size: number;
  total_count: number;
}

interface ListReplayLogResponse {
  data: ListReplayLogData;
}

interface ListReplayLogParams {
  page_num?: number;
  page_size?: number;
  task_id?: string;
  request_id?: string;
  record_request_id?: string;
}

interface DiffReplayBodyRequest {
  request_id?: string;
  record_request_id?: string;
}

interface DiffReplayBodyItem {
  request: string;
  response: string;
}

interface DiffReplayBodyResponse {
  data: {
    sample: DiffReplayBodyItem;
    target: DiffReplayBodyItem;
  };
}

/**
 * 获取流量录制日志列表
 * @param params - 查询参数，如分页信息
 * @returns 返回日志列表数据的 Promise
 * @throws 如果网络请求或服务器处理失败，则抛出错误
 */
async function listRecordLog(
  params?: ListRecordLogParams
): Promise<ListRecordLogResponse> {
  return get<ListRecordLogResponse>(
    "/apis/v1/traffic/record/log/list",
    params as Record<string, string | number | boolean> | undefined
  );
}

/**
 * 获取流量录制日志列表
 * @param params - 查询参数，如分页信息
 * @returns 返回日志列表数据的 Promise
 * @throws 如果网络请求或服务器处理失败，则抛出错误
 */
async function getLogList(
  params?: ListRecordLogParams
): Promise<ListRecordLogResponse> {
  return get<ListRecordLogResponse>(
    "/apis/v1/traffic/replay/log/list",
    params as Record<string, string | number | boolean> | undefined
  );
}

async function listReplayLog(
  params?: ListReplayLogParams
): Promise<ListReplayLogResponse> {
  return get<ListReplayLogResponse>(
    "/apis/v1/traffic/replay/log/list",
    params as Record<string, string | number | boolean> | undefined
  );
}

async function diffReplayBody(
  data: DiffReplayBodyRequest
): Promise<DiffReplayBodyResponse> {
  return post<DiffReplayBodyResponse>(
    "/apis/v1/traffic/replay/request/diff",
    data
  );
}
/**
 * 创建回放任务请求参数
 */
interface CreateReplayTaskRequest {
  /**
   * 需要回放的请求 ID 列表
   */
  request_ids: string[];
  /**
   * 目标集群环境
   */
  cluster_env: string;
  /**
   * 是否忽略请求间隔 (可选)
   * @default false
   */
  skip_duration?: boolean;
  /**
   * 回放目标地址。为空时沿用原始请求 Host。
   */
  rewrite?: string;
}

/**
 * 创建流量回放任务
 * @param data - 请求参数，包含请求 ID 列表、目标环境以及是否忽略间隔
 * @returns 返回创建结果的 Promise (具体类型取决于 API 响应)
 * @throws 如果网络请求或服务器处理失败，则抛出错误
 */
async function createReplayTask(
  data: CreateReplayTaskRequest
): Promise<unknown> {
  return post("/apis/v1/traffic/replay/task/create", data);
}

/**
 * 暂停流量回放任务
 * @param data - 请求参数，包含回放任务 ID
 * @returns 返回暂停结果的 Promise (具体类型取决于 API 响应)
 * @throws 如果网络请求或服务器处理失败，则抛出错误
 */
async function pauseReplayTask(
  data: CreateReplayTaskRequest
): Promise<unknown> {
  return post("/apis/v1/traffic/replay/task/pause", data);
}

/**
 * 获取回放请求 cURL 命令的请求参数
 */
interface GetReplayRequestCurlRequest {
  /**
   * 请求 ID
   */
  request_id: string;
}

/**
 * cURL 命令响应数据
 */
interface ReplayRequestCurlData {
  /**
   * 生成的 cURL 命令字符串
   */
  command: string;
  /**
   * 允许未定义的其他属性
   */
  [property: string]: unknown;
}

/**
 * 获取回放请求 cURL 命令的完整响应体
 */
interface GetReplayRequestCurlResponse {
  data: ReplayRequestCurlData;
  /**
   * 允许未定义的其他属性
   */
  [property: string]: unknown;
}

/**
 * 获取指定请求 ID 对应的 cURL 命令
 * @param data - 请求参数，包含请求 ID
 * @returns 返回包含 cURL 命令的响应数据 Promise
 * @throws 如果网络请求或服务器处理失败，则抛出错误
 */
async function getReplayRequestCurl(
  data: GetReplayRequestCurlRequest
): Promise<GetReplayRequestCurlResponse> {
  return post<GetReplayRequestCurlResponse>(
    "/apis/v1/traffic/replay/request/curl",
    data
  );
}

/**
 * 集群环境列表中的单个条目
 */
interface ClusterEnvItem {
  business?: string;
  cluster_id: string;
  cluster_name: string;
  cluster_type: string;
  platform: string;
  cluster_env?: string;
  clusterEnv?: string; // 兼容 OpenAPI 中的 camelCase 字段
  kube_content: string;
  remark: string;
  /**
   * 允许未定义的其他属性
   */
  [property: string]: unknown;
}

/**
 * 集群环境列表数据结构 (包含分页和条目)
 */
interface ClusterEnvListData {
  // 重命名并更新结构
  items: ClusterEnvItem[];
  page_num: number;
  page_size: number;
  total_count: number;
  /**
   * 允许未定义的其他属性
   */
  [property: string]: unknown;
}

/**
 * 获取集群环境列表的完整响应体
 */
interface ClusterEnvListResponse {
  // 更新 data 字段的类型引用
  data: ClusterEnvListData;
  /**
   * 允许未定义的其他属性
   */
  [property: string]: unknown;
}

/**
 * 获取集群环境列表
 * @returns 返回包含环境名称列表的响应数据 Promise
 * @throws 如果网络请求或服务器处理失败，则抛出错误
 */
async function listClusterEnvs(): Promise<ClusterEnvListResponse> {
  return get<ClusterEnvListResponse>("/apis/v1/cluster/env/list");
}

interface ListClustersParams {
  business?: string;
  cluster_env?: string;
  status?: boolean;
  page_size?: number;
  page_num?: number;
}

interface ClusterItem {
  cluster_id: string;
  cluster_name: string;
  cluster_type: string;
  platform: string;
  cluster_env?: string;
  clusterEnv?: string;
  kube_content: string;
  remark: string;
  [property: string]: unknown;
}

interface ClusterListData {
  items: ClusterItem[];
  page_num: number;
  page_size: number;
  total_count: number;
}

interface ListClustersResponse {
  data: ClusterListData;
}

interface DetailClusterResponse {
  data: ClusterItem;
}

interface CreateClusterRequest {
  cluster_name: string;
  cluster_type: string;
  platform: string;
  cluster_env: string;
  kube_content: string;
  remark: string;
}

interface ClusterBindingItem {
  cluster_id: string;
  business: string;
  cluster_env: string;
  cluster_name?: string;
  cluster_type?: string;
  platform?: string;
  remark?: string;
  [property: string]: unknown;
}

interface ClusterBindingListData {
  items: ClusterBindingItem[];
  page_num: number;
  page_size: number;
  total_count: number;
}

interface ListClusterBindingsResponse {
  data: ClusterBindingListData;
}

interface CreateClusterBindingRequest {
  cluster_id: string;
  business: string;
  cluster_env: string;
}

interface VirtualHostItem {
  business: string;
  cluster_env: string;
  host: string;
  remark?: string;
  [property: string]: unknown;
}

interface VirtualHostListData {
  items: VirtualHostItem[];
  page_num: number;
  page_size: number;
  total_count: number;
}

interface ListVirtualHostsResponse {
  data: VirtualHostListData;
}

interface CreateVirtualHostRequest {
  business: string;
  cluster_env: string;
  host: string;
  remark?: string;
}

async function listClusters(
  params?: ListClustersParams
): Promise<ListClustersResponse> {
  return get<ListClustersResponse>(
    "/apis/v1/cluster/list",
    params as Record<string, string | number | boolean> | undefined
  );
}

async function detailCluster(clusterId: string): Promise<DetailClusterResponse> {
  return get<DetailClusterResponse>("/apis/v1/cluster/detail", {
    cluster_id: clusterId,
  });
}

async function createCluster(data: CreateClusterRequest): Promise<unknown> {
  return post("/apis/v1/cluster/create", data);
}

async function listClusterBindings(
  params?: ListClustersParams
): Promise<ListClusterBindingsResponse> {
  return get<ListClusterBindingsResponse>(
    "/apis/v1/cluster/binding/list",
    params as Record<string, string | number | boolean> | undefined
  );
}

async function createClusterBinding(
  data: CreateClusterBindingRequest
): Promise<unknown> {
  return post("/apis/v1/cluster/binding/create", data);
}

async function listVirtualHosts(
  params?: ListClustersParams
): Promise<ListVirtualHostsResponse> {
  return get<ListVirtualHostsResponse>(
    "/apis/v1/virtual-host/list",
    params as Record<string, string | number | boolean> | undefined
  );
}

async function createVirtualHost(
  data: CreateVirtualHostRequest
): Promise<unknown> {
  return post("/apis/v1/virtual-host/create", data);
}

/**
 * 获取录制任务列表请求参数
 */
interface ListRecordTasksParams {
  /**
   * 页码
   */
  page_number?: number;
  /**
   * 每页数量
   */
  page_size?: number;
}

/**
 * 录制任务条目
 */
interface RecordTaskItem {
  /**
   * 环境
   */
  cluster_env: string;
  /**
   * 创建时间
   */
  created_at: string;
  /**
   * 状态: true 已结束, false 进行中
   */
  status: boolean;
  /**
   * 任务 ID
   */
  task_id: string;
  /**
   * 产品线
   */
  business: string;
  /**
   * 环境名称
   */
  env_name: string;
  /**
   * 环境描述
   */
  env_description?: string;
  /**
   * 请求头配置
   */
  request_headers?: Record<string, string>;
}

/**
 * 录制任务列表数据结构
 */
interface RecordTaskListData {
  items: RecordTaskItem[];
  page_num: number;
  page_size: number;
  total_count: number;
}

/**
 * 获取录制任务列表的完整响应体
 */
interface ListRecordTasksResponse {
  data: RecordTaskListData;
}

/**
 * 获取流量录制任务列表
 * @param params - 查询参数，如分页信息
 * @returns 返回任务列表数据的 Promise
 * @throws 如果网络请求或服务器处理失败，则抛出错误
 */
async function listRecordTasks(
  params?: ListRecordTasksParams
): Promise<ListRecordTasksResponse> {
  // 将 page_number 转换为 API 接受的参数名 (如果需要)
  const apiParams: Record<string, string | number | boolean> = {};
  if (params?.page_number !== undefined) {
    apiParams.page_number = params.page_number; // 确认 API 参数名
  }
  if (params?.page_size !== undefined) {
    apiParams.page_size = params.page_size;
  }
  return get<ListRecordTasksResponse>(
    "/apis/v1/traffic/record/task/list",
    apiParams
  );
}

export {
  createRecordTask,
  pauseRecordTask,
  listRecordLog,
  createReplayTask,
  pauseReplayTask,
  getReplayRequestCurl,
  listClusterEnvs,
  listClusters,
  detailCluster,
  createCluster,
  listClusterBindings,
  createClusterBinding,
  listVirtualHosts,
  createVirtualHost,
  listRecordTasks,
  getLogList,
  listReplayLog,
  diffReplayBody,
};
export type {
  CreateRecordTaskRequest,
  RecordLogItem,
  ListRecordLogData,
  ListRecordLogResponse,
  ListRecordLogParams,
  ReplayLogItem,
  ListReplayLogData,
  ListReplayLogResponse,
  ListReplayLogParams,
  DiffReplayBodyRequest,
  DiffReplayBodyItem,
  DiffReplayBodyResponse,
  CreateReplayTaskRequest,
  GetReplayRequestCurlRequest,
  ReplayRequestCurlData,
  GetReplayRequestCurlResponse,
  ClusterEnvItem,
  ClusterEnvListData,
  ClusterEnvListResponse,
  ListClustersParams,
  ClusterItem,
  ClusterListData,
  ListClustersResponse,
  DetailClusterResponse,
  CreateClusterRequest,
  ClusterBindingItem,
  ClusterBindingListData,
  ListClusterBindingsResponse,
  CreateClusterBindingRequest,
  VirtualHostItem,
  VirtualHostListData,
  ListVirtualHostsResponse,
  CreateVirtualHostRequest,
  ListRecordTasksParams,
  RecordTaskItem,
  RecordTaskListData,
  ListRecordTasksResponse,
};
